import { NewsroomStyleChecker } from '@bloxdigital/newsroom-style-guide';
import type { StyleRule, StyleViolation } from '@bloxdigital/newsroom-style-guide';
import { getAiToolkit } from '@tiptap-pro/ai-toolkit';
import type { Editor } from '@tiptap/core';
import type { Node as PmNode } from '@tiptap/pm/model';
import { Decoration } from '@tiptap/pm/view';
import { parseOperationMeta, type ProofreaderOperationMeta } from './types';

// ---------------------------------------------------------------------------
// Checker singleton
// ---------------------------------------------------------------------------
let currentChecker: NewsroomStyleChecker ;

async function initializeChecker(): Promise<NewsroomStyleChecker > {
  if (!currentChecker) {
    const checker = new NewsroomStyleChecker ({ preset: 'complete' });
    await checker.ready();
    currentChecker = checker;
  }
  return currentChecker;
}

// ---------------------------------------------------------------------------
// Category styles
// ---------------------------------------------------------------------------

const CATEGORY_STYLES: Record<string, { underline: string; background: string }> = {
  abbreviations: { underline: '#1976D2', background: '#E3F2FD' },
  capitalization: { underline: '#7B1FA2', background: '#F3E5F5' },
  numbers: { underline: '#F57C00', background: '#FFF3E0' },
  'dates-times': { underline: '#00796B', background: '#E0F2F1' },
  punctuation: { underline: '#D32F2F', background: '#FFEBEE' },
  'common-errors': { underline: '#E64A19', background: '#FBE9E7' },
};

function getCategoryFromRuleId(ruleId: string): string {
  return ruleId.split('/')[0];
}

// ---------------------------------------------------------------------------
// Loop prevention
// ---------------------------------------------------------------------------

/**
 * Fingerprints from the previous check cycle.
 * Keyed by `ruleId:nodePos:original→fix`. If the reverse fingerprint exists,
 * the candidate violation is suppressed to prevent toggle loops.
 */
let previousFingerprints = new Set<string>();

function makeFingerprint(
  ruleId: string,
  nodePos: number,
  original: string,
  fix: string,
): string {
  return `${ruleId}:${nodePos}:${original}→${fix}`;
}

// ---------------------------------------------------------------------------
// Per-node info with positions
// ---------------------------------------------------------------------------

interface HashedNodeInfo {
  hash: string;
  text: string;
  /** ProseMirror position where the node starts in the document. */
  nodeStart: number;
  node: PmNode;
}

/**
 * Walk the ProseMirror document and collect every node that has a `_hash`
 * attribute, along with its document position. The position is needed to
 * convert text-level violation offsets into ProseMirror positions.
 */
function collectHashedNodes(doc: PmNode): HashedNodeInfo[] {
  const nodes: HashedNodeInfo[] = [];
  doc.descendants((node, pos) => {
    const hash: string | undefined = node.attrs?._hash;
    if (hash) {
      nodes.push({ hash, text: node.textContent, nodeStart: pos, node });
      return false; // don't descend further into this node
    }
  });
  return nodes;
}

// ---------------------------------------------------------------------------
// Text-offset → ProseMirror position mapping
// ---------------------------------------------------------------------------

/**
 * Convert a character offset within `node.textContent` to an absolute
 * ProseMirror document position.
 *
 * ProseMirror positions account for node boundaries (each opening/closing
 * tag consumes one position). For a block node starting at `nodeStart`,
 * the first text character is at `nodeStart + 1`. We then walk the node's
 * inline children to skip over non-text inline nodes (images, etc.) that
 * occupy positions but don't contribute characters to `textContent`.
 */
function textOffsetToPos(node: PmNode, nodeStart: number, offset: number): number {
  let pos = nodeStart + 1; // +1 for the opening of the block node
  let charsSeen = 0;

  for (let i = 0; i < node.childCount; i++) {
    const child = node.child(i);
    if (child.isText) {
      const len = child.text!.length;
      if (charsSeen + len >= offset) {
        return pos + (offset - charsSeen);
      }
      charsSeen += len;
      pos += len;
    } else {
      // Inline non-text node (e.g. image, hard break) — occupies positions
      // but contributes nothing to textContent.
      pos += child.nodeSize;
    }
  }

  // Fallback: return end of node content
  return pos;
}

// ---------------------------------------------------------------------------
// Unique ID generation
// ---------------------------------------------------------------------------
let suggestionCounter = 0;
function nextSuggestionId(): string {
  suggestionCounter += 1;
  return `sg-${suggestionCounter}`;
}

// ---------------------------------------------------------------------------
// Style-check pipeline
// ---------------------------------------------------------------------------

/**
 * Runs the style-check pipeline using the AI Toolkit's `setSuggestions` API
 * to create one Suggestion per violation with precise character ranges.
 *
 * Previous approach used `proofreaderWorkflow` which diffs original vs
 * modified doc — but its diff utility merges nearby inline changes into a
 * single suggestion (via `changeMergeDistance`), causing the entire paragraph
 * to be highlighted with only one rule's metadata.
 *
 * By creating suggestions directly we:
 * - Highlight only the exact violating text (e.g. "Calif." not the whole ¶)
 * - Preserve per-violation metadata (rule, category, severity, message)
 * - Support multiple overlapping rule categories within a single node
 */
async function runProofreaderCheck(editor: Editor): Promise<void> {
  const checker = await initializeChecker();
  const toolkit = getAiToolkit(editor);
  const doc = editor.state.doc;

  // Step 1: Collect hashed nodes with positions
  const hashedNodes = collectHashedNodes(doc);

  if (hashedNodes.length === 0) {
    toolkit.setSuggestions([]);
    return;
  }

  // Pre-build rules lookup
  const rulesMap = new Map<string, StyleRule>();
  for (const r of checker.getRules()) {
    rulesMap.set(r.id, r);
  }

  // Step 2: Check each node and build Suggestion objects
  const suggestions: Parameters<ReturnType<typeof getAiToolkit>['setSuggestions']>[0] =
    [];
  const currentFingerprints = new Set<string>();

  for (const nodeInfo of hashedNodes) {
    if (!nodeInfo.text.trim()) continue;

    const violations: StyleViolation[] = await checker.check(nodeInfo.text);

    // Sort violations by start offset for consistent processing
    const sorted = [...violations].sort((a, b) => a.start - b.start);

    // Track occupied ranges within this node to skip overlaps
    const occupiedRanges: Array<{ from: number; to: number }> = [];

    for (const violation of sorted) {
      if (!violation.fix || violation.fix === violation.original) continue;

      const rule = rulesMap.get(violation.ruleId);
      if (!rule) continue;

      // Loop prevention: skip if a reverse fingerprint was present last cycle
      const reverseFingerprint = makeFingerprint(
        violation.ruleId,
        nodeInfo.nodeStart,
        violation.fix,
        violation.original,
      );
      if (previousFingerprints.has(reverseFingerprint)) continue;

      // Convert text offsets → ProseMirror positions
      const pmFrom = textOffsetToPos(nodeInfo.node, nodeInfo.nodeStart, violation.start);
      const pmTo = textOffsetToPos(nodeInfo.node, nodeInfo.nodeStart, violation.end);

      // Skip if this range overlaps an already-created suggestion
      // (overlapping suggestions are not displayed by the toolkit)
      const overlaps = occupiedRanges.some((r) => pmFrom < r.to && pmTo > r.from);
      if (overlaps) continue;
      occupiedRanges.push({ from: pmFrom, to: pmTo });

      const category = getCategoryFromRuleId(violation.ruleId);
      const meta: ProofreaderOperationMeta = {
        ruleId: rule.id,
        category,
        severity: rule.severity,
        message: violation.message,
        ruleTitle: rule.title || rule.id,
        ruleDescription: rule.description,
      };

      const suggestionId = nextSuggestionId();

      suggestions.push({
        id: suggestionId,
        range: { from: pmFrom, to: pmTo },
        replacementOptions: [
          {
            id: `${suggestionId}-fix`,
            content: violation.fix,
          },
        ],
        metadata: {
          source: 'ap-style',
          operationMeta: JSON.stringify(meta),
        },
        reviewMode: 'preview',
        displayOptions: {
          showReplacement: false,
          hideCurrentContent: false,
          renderDecorations: ({ range, suggestion, isSelected }) => {
            const opMeta = parseOperationMeta(suggestion.metadata?.operationMeta);
            const cat = opMeta?.category ?? 'common-errors';

            return [
              Decoration.inline(range.from, range.to, {
                class: [
                  'tiptap-ai-suggestion',
                  isSelected ? 'tiptap-ai-suggestion--selected' : '',
                  'style-guide-suggestion',
                  `style-category-${cat}`,
                ]
                  .filter(Boolean)
                  .join(' '),
                'data-style-category': cat,
                title: opMeta?.ruleTitle ?? '',
              }),
            ];
          },
        },
      });

      currentFingerprints.add(
        makeFingerprint(
          violation.ruleId,
          nodeInfo.nodeStart,
          violation.original,
          violation.fix,
        ),
      );
    }
  }

  // Update fingerprints for next cycle
  previousFingerprints = currentFingerprints;

  // Step 3: Set suggestions directly — each violation is its own suggestion
  // with a precise range covering only the offending text
  toolkit.setSuggestions(suggestions);
}

export { runProofreaderCheck, CATEGORY_STYLES, getCategoryFromRuleId };
